import React from 'react';
import PropTypes from 'prop-types';
import { xorBy } from 'lodash';
import { connect } from 'react-redux';
import { push } from 'react-router-redux';
import { SortableContainer, SortableElement, arrayMove, SortableHandle } from 'react-sortable-hoc';
import { activateModal, addHoveredProperty, clearHoveredProperty } from '../../store/actions';
import { saveBuilding, saveSite, saveItems } from '../../store/actions/favorites';
import { PROPERTY_TYPES } from '../../constants';
import { ViewerPrimaryColorUserStyle } from '../UserCustomization';
import ListMedia from './ListMedia';
import ListItemSummary from './ListItemSummary';
import ListItemLinks from './ListItemLinks';
import getPropertyIcon from '../../utils/getPropertyIcon';
import buildingMarker from '../Map/buildingMarker.png';
import siteMarker from '../Map/siteMarker.png';

const scrollToMap = () => {
  const el = document.getElementById('foldersMapRef');
  el.scrollIntoView();
};

const markerIcon = (property, propertyType, markers) => {
  const defaultIcon = propertyType === 'sites' ? siteMarker : buildingMarker;
  return getPropertyIcon(property, propertyType, defaultIcon, markers);
};

const DragHandle = SortableHandle(() => <span data-tip="Drag To Reorder" className="drag-handle">{'\u2630'}</span>);
const SortableItem = SortableElement(({
  handleClick,
  handleSaveClick,
  onDemographicsClick,
  onAddToFavoritesClick,
  handleModalOpen,
  onVideoClick,
  type,
  viewDetails,
  isActive,
  organizationId,
  marker,
  key,
  value,
  openMapPopup,
  markers,
  mapMarkerNumber,
}) => {
  return (<li key={key} className="sortable-item">
    <ListMedia
      type={type}
      property={value}
      handleClick={handleClick}
      onVideoClick={onVideoClick}
      isActive={isActive}
      marker={marker}
    />
    <div className="list-col-2">
      <a tabIndex="0" className="list-text__link" onClick={() => { handleClick(value._id); }}>
        <ListItemSummary
          type={type}
          property={value}
        />
      </a>
      <ListItemLinks
        property={value}
        type={type}
        saved
        organizationId={organizationId}
        onDemographicsClick={onDemographicsClick}
        onAddToFavoritesClick={onAddToFavoritesClick}
        handleModalOpen={handleModalOpen}
        handleSaveClick={handleSaveClick}
      />
      {viewDetails &&
        <ViewerPrimaryColorUserStyle domStyle="color">
          <a className="list-view-details" tabIndex="0" onClick={() => { handleClick(value._id); }}>
            View Details
          </a>
        </ViewerPrimaryColorUserStyle>
      }
    </div>
    <div className="list-col-3">
      <button data-tip="See On Map" onClick={() => { scrollToMap(); openMapPopup(value); }} className="icon-map-popup">
        <span className="map-marker-num">{mapMarkerNumber}</span>
        <img src={markerIcon(value, type, markers)} alt="" />
      </button>
      <DragHandle />
    </div>
  </li>);
});

const SortableList = SortableContainer(({
  handleClick,
  handleSaveClick,
  onDemographicsClick,
  onAddToFavoritesClick,
  handleModalOpen,
  onVideoClick,
  type,
  viewDetails,
  isActive,
  organizationId,
  marker,
  items,
  onSortEnd,
  openMapPopup,
  markers,
  mapMarkersStartIndex,
}) => {
  return (
    <ul className="favorites-list__items list-wrapper" data-bd-empty={`No saved ${type}`}>
      {items
        .filter(property => property.active)
        .map((property, index) => (
          <SortableItem
            key={property._id}
            index={index}
            value={property}
            items={items}
            onSortEnd={onSortEnd}
            useDragHandle
            type={type}
            handleClick={handleClick}
            handleSaveClick={handleSaveClick}
            onDemographicsClick={onDemographicsClick}
            onAddToFavoritesClick={onAddToFavoritesClick}
            handleModalOpen={handleModalOpen}
            onVideoClick={onVideoClick}
            viewDetails={viewDetails}
            isActive={isActive}
            organizationId={organizationId}
            marker={marker}
            openMapPopup={openMapPopup}
            markers={markers}
            mapMarkerNumber={parseInt(mapMarkersStartIndex, 0) + parseInt(index, 0) + 1}
          />
      ))}
    </ul>
  );
});

export class DraggableListComponent extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      items: props.properties,
    };

    this.onSortEnd = this.onSortEnd.bind(this);
  }

  componentDidUpdate(prevProps) {
    if (xorBy(this.props.properties, prevProps.properties, '_id').length > 0) {
      this.updateState(this.props.properties);
    }
  }

  onSortEnd = ({ oldIndex, newIndex }) => {
    const { items } = this.state;
    this.setState({
      items: arrayMove(items, oldIndex, newIndex),
    });
    this.props.savePropertyList(this.props.activeFolder, this.props.type, this.state.items);
  };

  updateState(items) {
    this.setState({
      items,
    });
  }

  render() {
    const {
      handleClick,
      handleSaveClick,
      onDemographicsClick,
      onAddToFavoritesClick,
      handleModalOpen,
      onVideoClick,
      type,
      viewDetails,
      isActive,
      organizationId,
      marker,
      openMapPopup,
      markers,
      mapMarkersStartIndex,
    } = this.props;

    return (
      <SortableList
        items={this.state.items}
        onSortEnd={this.onSortEnd}
        useDragHandle
        type={type}
        handleClick={handleClick}
        handleSaveClick={handleSaveClick}
        onDemographicsClick={onDemographicsClick}
        onAddToFavoritesClick={onAddToFavoritesClick}
        handleModalOpen={handleModalOpen}
        onVideoClick={onVideoClick}
        viewDetails={viewDetails}
        isActive={isActive}
        organizationId={organizationId}
        marker={marker}
        index={Math.random(1000)}
        openMapPopup={openMapPopup}
        markers={markers}
        mapMarkersStartIndex={mapMarkersStartIndex}
      />
    );
  }
}

DraggableListComponent.propTypes = {
  type: PropTypes.string.isRequired,
  handleClick: PropTypes.func.isRequired,
  handleSaveClick: PropTypes.func.isRequired,
  onDemographicsClick: PropTypes.func.isRequired,
  onAddToFavoritesClick: PropTypes.func.isRequired,
  savePropertyList: PropTypes.func,
  onVideoClick: PropTypes.func.isRequired,
  handleModalOpen: PropTypes.func,
  viewDetails: PropTypes.bool,
  isActive: PropTypes.bool,
  organizationId: PropTypes.string,
  marker: PropTypes.bool,
  properties: PropTypes.arrayOf(PropTypes.object),
  activeFolder: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  openMapPopup: PropTypes.func.isRequired,
  markers: PropTypes.object,
  mapMarkersStartIndex: PropTypes.number,
};

DraggableListComponent.defaultProps = {
  mapMarkersStartIndex: 0,
};

function mapStateToProps(state) {
  return {
    organizationId: state.organizationId,
  };
}

/* istanbul ignore next */
function mapDispatchToProps(dispatch, { type, handleClick }) {
  const saver = type === PROPERTY_TYPES.BUILDINGS ? saveBuilding : saveSite;
  return {
    handleClick: id => {
      dispatch(push(`/${type}/${id}${window.location.search}`));
      if (handleClick) {
        handleClick(id);
      }
    },
    onDemographicsClick: () => { dispatch(activateModal('report')); },
    onAddToFavoritesClick: () => { dispatch(activateModal('folders')); },
    onVideoClick: id => { dispatch(activateModal(`video-${id}`)); },
    handleSaveClick: id => { dispatch(saver(id)); },
    savePropertyList: (folderId, propertyType, list) =>
      dispatch(saveItems(list, propertyType, folderId)),
    openMapPopup: property => dispatch(addHoveredProperty(property)),
    closeMapPopup: () => dispatch(clearHoveredProperty()),
  };
}

export default connect(mapStateToProps, mapDispatchToProps)(DraggableListComponent);



// WEBPACK FOOTER //
// ./src/components/List/DraggableList.js